/**
 * React Query hooks for Receipts
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { receiptsService } from "../services/receipts.service";
import type { CreateReceiptRequest, FilterParams } from "../types";
import { toast } from "sonner";

export const useReceipts = (params?: FilterParams) => {
  return useQuery({
    queryKey: ["receipts", params],
    queryFn: () => receiptsService.getReceipts(params),
  });
};

export const useReceipt = (receiptId: string) => {
  return useQuery({
    queryKey: ["receipts", receiptId],
    queryFn: () => receiptsService.getReceiptById(receiptId),
    enabled: !!receiptId,
  });
};

export const useCreateReceipt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateReceiptRequest) => receiptsService.createReceipt(data),
    onSuccess: (data) => {
      toast.success(data.message || "Receipt created successfully");
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      queryClient.invalidateQueries({ queryKey: ["outstanding"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create receipt");
    },
  });
};

export const useUpdateReceipt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ receiptId, data }: { receiptId: string; data: Partial<any> }) =>
      receiptsService.updateReceipt(receiptId, data),
    onSuccess: () => {
      toast.success("Receipt updated successfully");
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      queryClient.invalidateQueries({ queryKey: ["outstanding"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update receipt");
    },
  });
};

export const useDeleteReceipt = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (receiptId: string) => receiptsService.deleteReceipt(receiptId),
    onSuccess: () => {
      toast.success("Receipt deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      queryClient.invalidateQueries({ queryKey: ["outstanding"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete receipt");
    },
  });
};

export const useReceiptSummary = (params?: { startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ["receipts", "summary", params],
    queryFn: () => receiptsService.getReceiptSummary(params),
  });
};

